import type { ChartBlock, ChartRow } from "../types";

export function parseAlign(cell: string): "left" | "right" | "center" {
  const c = cell.trim();
  const left = c.startsWith(":");
  const right = c.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  return "left";
}

export function splitTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

export function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

export function plainText(md: string): string {
  return md
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, "$1")
    .trim();
}

export type ColumnKind = "rank" | "status" | "type" | "text";

export const RANK_HEADER = /^(#|no\.?|s[.\/]?\s?no\.?|sr\.?|sl\.?|rank|s\/n|序号)$/i;
const STATUS_HEADER = /\b(status|state|severity|result|health)\b/i;
const TYPE_HEADER = /\b(type|category|kind|classification)\b/i;

export function detectColumnKinds(
  header: string[],
  rows: string[][],
  colCount: number,
): ColumnKind[] {
  return Array.from({ length: colCount }, (_, i) => {
    const h = plainText(header[i] ?? "").trim();
    if (RANK_HEADER.test(h)) return "rank";
    if (STATUS_HEADER.test(h)) return "status";
    if (TYPE_HEADER.test(h)) return "type";
    if (i === 0 && !h) {
      const vals = rows.map((r) => plainText(r[i] ?? "").trim());
      if (vals.length > 0 && vals.every((v) => /^\d{1,3}$/.test(v))) return "rank";
    }
    return "text";
  });
}

const MAX_CHART_ROWS = 24;

function parseNumber(raw: string): number | null {
  const s = plainText(raw).trim();
  if (!s) return null;
  const cleaned = s.replace(/[$£€,\s%]/g, "");
  if (!/^[-+]?(?:\d+\.?\d*|\.\d+)$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// tableToChartBlock: a rank/index column ("#", "No.") is never a series and never the x-axis.
export function tableToChartBlock(header: string[], rows: string[][]): ChartBlock | null {
  if (rows.length < 2 || rows.length > MAX_CHART_ROWS) return null;
  const colCount = Math.max(header.length, ...rows.map((r) => r.length), 1);

  const numericCols: number[] = [];
  const textCols: number[] = [];
  let percentCols = 0;
  for (let c = 0; c < colCount; c++) {
    const cells = rows.map((r) => plainText(r[c] ?? "").trim());
    const nonEmpty = cells.filter((v) => v.length > 0);
    const h = plainText(header[c] ?? "").trim();
    const allNumeric = nonEmpty.length > 0 && nonEmpty.every((v) => parseNumber(v) !== null);
    if (allNumeric) {
      if (RANK_HEADER.test(h)) continue;
      numericCols.push(c);
      if (nonEmpty.every((v) => v.includes("%"))) percentCols++;
    } else {
      textCols.push(c);
    }
  }
  if (numericCols.length === 0) return null;

  let catCol = -1;
  let bestDistinct = -1;
  for (const c of textCols) {
    const distinct = new Set(rows.map((r) => plainText(r[c] ?? "").trim())).size;
    if (distinct > bestDistinct) {
      bestDistinct = distinct;
      catCol = c;
    }
  }
  if (catCol === -1 || bestDistinct < 2) return null;

  const catKey = plainText(header[catCol] ?? "").trim() || "label";
  const series = numericCols.map((c, i) => {
    const name = plainText(header[c] ?? "").trim() || `Series ${i + 1}`;
    const key = name === catKey ? `${name} (${i + 1})` : name;
    return { key, label: name };
  });

  const data: ChartRow[] = rows.map((r) => {
    const row: ChartRow = { [catKey]: plainText(r[catCol] ?? "").trim() };
    numericCols.forEach((c, i) => {
      row[series[i].key] = parseNumber(r[c] ?? "") ?? 0;
    });
    return row;
  });

  return {
    type: "chart",
    kind: "auto",
    xKey: catKey,
    series,
    data,
    ...(percentCols === numericCols.length ? { unit: "%" } : {}),
  };
}
